import { format, parseISO, subDays, subMonths, subWeeks } from 'date-fns'
import fr from 'date-fns/locale/fr'
import { http } from '@/services/api.service'
import OneGroup from '@/pages/Surveys/Modals/OneGroup.vue'
import DashboardArticleModal from '@/components/dashboard/DashboardArticleModal.vue'
import { getDomainPeriodStartDate } from './domainPeriod'

const EMPLOYEE_ACTIVITY_TOTAL_KEYS = [
  'workforce',
  'downloadsPeriod',
  'downloadsCumulative',
  'downloadRateWorkforce',
  'downloadRateAverage',
  'activeLogins',
  'activeUsers',
  'activeUsersRowSum',
  'activeUsersRateDownloads',
  'activeUsersRateAverage'
]

function createEmptyEmployeeActivityTotals () {
  return EMPLOYEE_ACTIVITY_TOTAL_KEYS.reduce((totals, key) => {
    totals[key] = 0
    return totals
  }, {})
}

function normalizeEmployeeActivityTotals (totals) {
  return EMPLOYEE_ACTIVITY_TOTAL_KEYS.reduce((result, key) => {
    const value = Number(totals?.[key])
    result[key] = Number.isFinite(value) && value >= 0 ? value : 0
    return result
  }, {})
}

function normalizeEmployeeActivityDailySeries (series) {
  const rows = Array.isArray(series) ? series : []
  return rows.map(row => ({
    date: row?.date || '',
    downloads: Number(row?.downloads || 0),
    downloadRateWorkforce: Number(row?.downloadRateWorkforce || 0),
    downloadRateAverage: Number(row?.downloadRateAverage || 0),
    activeLogins: Number(row?.activeLogins || 0),
    activeUsers: Number(row?.activeUsers || 0),
    activeUsersRateDownloads: Number(row?.activeUsersRateDownloads || 0),
    activeUsersRateAverage: Number(row?.activeUsersRateAverage || 0)
  }))
}

export default {
  onDomainChange (newDomain = this.domain, oldDomain = null, options = {}) {
    const { force = false } = options
    const domain = newDomain || this.domain
    if (!domain || !domain.uuid) {
      this.lastLoadedDomainUuid = null
      return
    }
    if (!force && oldDomain && oldDomain.uuid === domain.uuid) {
      return
    }
    if (!force && this.lastLoadedDomainUuid === domain.uuid) {
      return
    }

    this.lastLoadedDomainUuid = domain.uuid
    this.clearArticleGroupsCache()
    if (this._surveyGroupDetailsCache) {
      this._surveyGroupDetailsCache = new Map()
    }
    this.loadingGraphs = true
    const keepStart = this.activeQuickRangeId === 'full'
    this.setDomainStartDate(keepStart).then((isCurrentDomain) => {
      if (isCurrentDomain) {
        this.getStats()
      }
    })
    this.fetchPendingNews()
    this.fetchMavieproNews()
    this.refreshSurveysSnapshot({ includePast: true })
    this.fetchMavieproSurveys()
    this.pendingSurveysTotal = 0
    this.activeSurveysTotal = 0
    this.lastEndedSurveyRaw = null
    this.topNewsItems = []
    this.loadingTopNews = true
    this.loginsSeries = []
    this.resetEmployeeActivitySnapshot()
    this.readTrackingDailySeries = []
    this.readTrackingTotals = null
    this.monthlyTrackingSeries = []
    this.isMonthlyTrackingOpen = false
    this.isMonthlyTrackingLoading = false
    this.loadingReadTracking = true
    this.loadingPopularities = true
    this.mockEmployeeTargets.totalEmployees = null
  },
  fetchPendingNews () {
    this.loadingPendingNews = true
    return http
      .get('/dashboard/articles/inactive')
      .then(({ data }) => {
        const items = Array.isArray(data?.items) ? data.items : []
        const limited = items.slice(0, 3).map((item, index) => {
          if (!item || typeof item !== 'object') {
            return item
          }
          return {
            ...item,
            _fetchedOrder: index
          }
        })

        this.pendingNewsRaw = limited
      })
      .catch(() => {
        this.pendingNewsRaw = []
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les actualités à activer pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
      .finally(() => {
        this.loadingPendingNews = false
      })
  },
  filterMediaMvpEntries (entries) {
    const slug = this.application?.app?.slug
    const domainUuid = this.domain?.uuid
    if (!Array.isArray(entries)) {
      return []
    }
    if (!slug || !['maviepro', 'tract'].includes(slug)) {
      return entries.filter(entry => {
        if (!domainUuid) {
          return true
        }
        const duplicatedBy = Array.isArray(entry?.duplicated_by) ? entry.duplicated_by : []
        return !duplicatedBy.includes(domainUuid)
      })
    }
    return entries.filter((entry) => {
      const visibility = entry?.media_mvp_visibility || 'both'
      if (!(visibility === 'both' || visibility === slug)) {
        return false
      }
      if (!domainUuid) {
        return true
      }
      const duplicatedBy = Array.isArray(entry?.duplicated_by) ? entry.duplicated_by : []
      return !duplicatedBy.includes(domainUuid)
    })
  },
  fetchMavieproNews () {
    this.loadingMavieproNews = true
    return http
      .get('/dashboard/news/maviepro', {
        params: {
          limit: 30
        }
      })
      .then(({ data }) => {
        const items = Array.isArray(data?.items) ? data.items : []
        this.mavieproNewsRaw = this.filterMediaMvpEntries(items).slice(0, 5)
      })
      .catch(() => {
        this.mavieproNewsRaw = []
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les actualités Maviepro pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
      .finally(() => {
        this.loadingMavieproNews = false
      })
  },
  refreshSurveysSnapshot ({ includePast = false, pendingLimit = 4, activeLimit = includePast ? 6 : 4 } = {}) {
    this.loadingPendingSurveys = true
    this.loadingActiveSurveys = true

    const params = {
      includePast,
      pendingLimit,
      activeLimit
    }

    return http
      .get('/dashboard/surveys/overview', { params })
      .then(({ data }) => {
        const pending = Array.isArray(data?.pending) ? data.pending : []
        const active = Array.isArray(data?.active) ? data.active : []

        this.pendingSurveysRaw = pending
        this.activeSurveysRaw = active
        const meta = data?.meta || {}
        this.surveysOverviewMeta = meta

        const pendingTotal = Number(meta.pendingTotal)
        this.pendingSurveysTotal = Number.isFinite(pendingTotal) && pendingTotal >= 0 ? pendingTotal : pending.length

        const activeTotal = Number(meta.activeTotal)
        this.activeSurveysTotal = Number.isFinite(activeTotal) && activeTotal >= 0 ? activeTotal : active.length

        this.lastEndedSurveyRaw = meta.lastEnded || null

        const workforceSize = Number(meta.workforceSize)
        if (Number.isFinite(workforceSize) && workforceSize >= 0) {
          if (!Number(this.mockEmployeeTargets.manualOverride)) {
            if (!Number.isFinite(this.mockEmployeeTargets.totalEmployees) || this.mockEmployeeTargets.totalEmployees < workforceSize) {
              this.mockEmployeeTargets.totalEmployees = workforceSize
            }
          }
        }
      })
      .catch(() => {
        this.pendingSurveysRaw = []
        this.activeSurveysRaw = []
        this.surveysOverviewMeta = null
        this.pendingSurveysTotal = 0
        this.activeSurveysTotal = 0
        this.lastEndedSurveyRaw = null

        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les sondages pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
      .finally(() => {
        this.loadingPendingSurveys = false
        this.loadingActiveSurveys = false
        this.$nextTick(() => {
          this.autoCollapseSurveysCard()
        })
        if (!Number(this.mockEmployeeTargets.manualOverride)) {
          const current = Number(this.mockEmployeeTargets.totalEmployees)
          if (!Number.isFinite(current) || current <= 0) {
            this.fetchEmployeeBase({ force: false })
          }
        }
      })
  },
  fetchMavieproSurveys () {
    this.loadingMavieproSurveys = true
    return http
      .get('/surveys/surveys-mvp')
      .then(({ data }) => {
        const entries = Array.isArray(data) ? data : []
        this.mavieproSurveysRaw = this.filterMediaMvpEntries(entries)
      })
      .catch(() => {
        this.mavieproSurveysRaw = []
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les sondages pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
      .finally(() => {
        this.loadingMavieproSurveys = false
        this.$nextTick(() => {
          this.autoCollapseSurveysCard()
        })
      })
  },
  autoCollapseSurveysCard () {
    if (!this.collapsedLines || !Object.prototype.hasOwnProperty.call(this.collapsedLines, 'surveys')) {
      return
    }
    if (this.loadingPendingSurveys || this.loadingMavieproSurveys) {
      return
    }
    const pendingCount = Array.isArray(this.pendingSurveys) ? this.pendingSurveys.length : 0
    const mavieproCount = Array.isArray(this.mavieproSurveys) ? this.mavieproSurveys.length : 0
    if (pendingCount + mavieproCount > 0 || this.collapsedLines.surveys === true) {
      return
    }
    this.$set(this.collapsedLines, 'surveys', true)
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('dash_collapsed_line_surveys', JSON.stringify(true))
      } catch (error) {
        // Silently ignore storage errors (private mode, quota, ...)
      }
    }
  },
  fetchPublishedNewsCount () {
    this.loadingPublishedNews = true
    this.newsPublishedCount = null
    const periodParams = this.buildDateRangeParams({ status: 'active' })

    const totalParams = { status: 'active' }

    return Promise.all([
      http.get('/article/count', { params: periodParams }).catch(() => ({ data: 0 })),
      http.get('/article/count', { params: totalParams }).catch(() => ({ data: 0 }))
    ])
      .then(([periodRes, totalRes]) => {
        const periodCount = Number(periodRes?.data)
        const totalCount = Number(totalRes?.data)
        this.newsPublishedCount = Number.isFinite(periodCount) && periodCount >= 0 ? periodCount : 0
        this.newsPublishedTotalCount = Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : this.newsPublishedCount
        if (this.domainStartDate && this.todayFormatted && this.dateStart && this.dateEnd) {
          const isFullRange = this.dateStart <= this.domainStartDate && this.dateEnd >= this.todayFormatted
          if (isFullRange) {
            this.newsPublishedCount = this.newsPublishedTotalCount
          }
        }
      })
      .catch(() => {
        if (!Number.isFinite(this.newsPublishedCount)) this.newsPublishedCount = 0
        if (!Number.isFinite(this.newsPublishedTotalCount)) this.newsPublishedTotalCount = this.newsPublishedCount
      })
      .finally(() => {
        this.loadingPublishedNews = false
      })
  },
  fetchDownloadsSnapshot () {
    const params = this.buildDateRangeParams()

    this.chartUsersData = null

    return http
      .get('/dashboard/downloads', { params })
      .then(({ data }) => {
        const totals = data?.totals || {}

        if (totals.allTime !== undefined && totals.allTime !== null) {
          const total = Number(totals.allTime)
          this.counters.total.users = Number.isFinite(total) && total >= 0 ? total : 0
        }

        if (totals.period !== undefined && totals.period !== null) {
          const period = Number(totals.period)
          this.counters.period.users = Number.isFinite(period) && period >= 0 ? period : 0
        }

        const series = Array.isArray(data?.series) ? data.series : []
        if (!series.length) {
          this.chartUsersData = null
          return
        }

        const labels = []
        const cumulativeValues = []
        let runningTotal = 0

        series.forEach(point => {
          const isoDate = point?.date || ''
          const count = Number(point?.count ?? 0)
          const safeValue = Number.isFinite(count) && count >= 0 ? count : 0
          runningTotal += safeValue

          const parsed = isoDate ? parseISO(isoDate) : null
          const isValidDate = parsed instanceof Date && !Number.isNaN(parsed?.getTime())
          const label = isValidDate
            ? format(parsed, 'dd/MM', { locale: fr })
            : isoDate

          labels.push(label)
          cumulativeValues.push(runningTotal)
        })

        this.chartUsersData = {
          labels,
          datasets: [
            {
              label: 'Téléchargements',
              borderColor: '#162DF3',
              backgroundColor: '#ecf4fe',
              pointBackgroundColor: '#162DF3',
              borderWidth: 1,
              radius: 1,
              fill: true,
              data: cumulativeValues
            }
          ]
        }
      })
      .catch(() => {
        this.chartUsersData = null
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les téléchargements pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
  },
  fetchLoginsSnapshot () {
    const params = this.buildDateRangeParams()

    this.updateToday()

    this.loginsSeries = []
    this.chartLoginsData = null
    this.counters.period.loginsUniq = 0

    return http
      .get('/dashboard/logins', { params })
      .then(({ data }) => {
        const totals = data?.totals || {}

        if (totals.period !== undefined && totals.period !== null) {
          const period = Number(totals.period)
          this.counters.period.loginsUniq = Number.isFinite(period) && period >= 0 ? period : 0
        }

        let series = Array.isArray(data?.series) ? data.series : []
        const fullRangeSelected = this.activeQuickRangeId === 'full' || this.isFullRangeSelected()
        if (series.length && fullRangeSelected) {
          const firstActiveIndex = series.findIndex(point => {
            const uniqueValue = Number(point?.unique ?? point?.total ?? point?.count ?? 0)
            return Number.isFinite(uniqueValue) && uniqueValue > 0
          })
          if (firstActiveIndex > 0) {
            series = series.slice(firstActiveIndex)
          }
        }

        this.loginsSeries = series
        if (!series.length) {
          this.chartLoginsData = null
          return
        }

        const labels = []
        const values = []

        series.forEach(point => {
          const isoDate = point?.date || ''
          const unique = Number(point?.unique ?? 0)
          const safeValue = Number.isFinite(unique) && unique >= 0 ? unique : 0

          const parsed = isoDate ? parseISO(isoDate) : null
          const isValidDate = parsed instanceof Date && !Number.isNaN(parsed?.getTime())
          const label = isValidDate
            ? format(parsed, 'dd/MM', { locale: fr })
            : isoDate

          labels.push(label)
          values.push(safeValue)
        })

        this.chartLoginsData = {
          labels,
          datasets: [
            {
              label: 'Connexions',
              backgroundColor: '#2D5DFB',
              borderColor: '#2D5DFB',
              data: values
            }
          ]
        }
      })
      .catch(() => {
        this.chartLoginsData = null
        this.loginsSeries = []
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les connexions pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
  },
  fetchTopNewsSnapshot () {
    const params = this.buildDateRangeParams({
      limit: 5
    })

    this.loadingTopNews = true
    this.topNewsItems = []

    return http
      .get('/dashboard/news', { params })
      .then(({ data }) => {
        const items = Array.isArray(data?.items) ? data.items : []
        this.topNewsItems = items.map(item => ({
          uuid: item?.uuid || null,
          title: item?.title || 'Actualité',
          views: Number(item?.views ?? 0)
        }))

        const totals = data?.totals || {}
        if (totals.period !== undefined && totals.period !== null) {
          const period = Number(totals.period)
          if (Number.isFinite(period) && period >= 0) {
            this.counters.period.newsViews = period
          }
        }
        if (totals.allTime !== undefined && totals.allTime !== null) {
          const allTime = Number(totals.allTime)
          if (Number.isFinite(allTime) && allTime >= 0) {
            this.counters.total.newsViews = allTime
          }
        } else if (totals.total !== undefined && totals.total !== null) {
          const allTime = Number(totals.total)
          if (Number.isFinite(allTime) && allTime >= 0) {
            this.counters.total.newsViews = allTime
          }
        }
      })
      .catch(() => {
        this.topNewsItems = []
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les actualités populaires pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
      .finally(() => {
        this.loadingTopNews = false
      })
  },
  fetchEmployeeBase ({ force = false } = {}) {
    if (!force) {
      const current = Number(this.mockEmployeeTargets.totalEmployees)
      if (Number.isFinite(current) && current > 0) {
        return
      }
    }

    if (!Number(this.mockEmployeeTargets.manualOverride)) {
      this.mockEmployeeTargets.totalEmployees = null
    }

    http
      .get('/dashboard/users/count')
      .then(({ data }) => {
        const total = Number(data?.count ?? 0)
        if (Number.isFinite(total) && total >= 0) {
          this.mockEmployeeTargets.totalEmployees = total
        }
      })
      .catch(() => {
        const fallback = Number(this.counters?.total?.users ?? 0)
        if (!Number.isFinite(this.mockEmployeeTargets.totalEmployees)) {
          this.mockEmployeeTargets.totalEmployees = fallback
        }
      })
  },
  async ensureArticleGroups () {
    if (!this._dashboardArticleGroupsCache) {
      try {
        const { data } = await http.get('/groups')
        this._dashboardArticleGroupsCache = Array.isArray(data) ? data : []
      } catch (error) {
        this._dashboardArticleGroupsCache = null
        throw error
      }
    }
    return this._dashboardArticleGroupsCache || []
  },
  clearArticleGroupsCache () {
    this._dashboardArticleGroupsCache = null
  },
  async openArticleModalOnDashboard ({ article = null, articleUuid = null, successMessage = null } = {}) {
    const groups = await this.ensureArticleGroups()
    const modal = this.$buefy.modal.open({
      parent: this,
      component: DashboardArticleModal,
      hasModalCard: false,
      width: 960,
      destroyOnHide: false,
      ariaRole: 'dialog',
      ariaModal: true,
      customClass: 'dashboard-modal dashboard-modal--article content-editor-modal-host',
      props: {
        article: article || null,
        articleKey: articleUuid || (article && article.uuid) || null,
        groups,
        stayOnDashboard: true
      },
      events: {
        refresh: (payload) => {
          if (successMessage) {
            this.$buefy.notification.open({
              duration: 4000,
              message: successMessage,
              position: 'is-bottom-right',
              type: 'is-success',
              hasIcon: true,
              icon: 'check'
            })
          }
          this.fetchPendingNews()
          this.fetchMavieproNews().catch(() => {})
          this.fetchDashboardArticlesSnapshot && this.fetchDashboardArticlesSnapshot()
          modal.close()
        }
      }
    })
    return modal
  },
  async createArticle () {
    if (!this.hasNewsEditionRights) {
      this.showPermissionDenied('Vous n\'avez pas les droits pour créer une actualité.')
      return
    }
    if (this.isOpeningArticleModal) {
      return
    }
    this.isOpeningArticleModal = true
    try {
      await this.openArticleModalOnDashboard({
        article: null,
        articleUuid: null,
        successMessage: 'Actualité enregistrée avec succès !'
      })
    } catch (error) {
      this.$buefy.notification.open({
        duration: 4000,
        message: 'Impossible d\'ouvrir la création d\'actualité pour le moment.',
        position: 'is-top-right',
        type: 'is-danger',
        hasIcon: true,
        iconPack: 'fas',
        icon: 'fas fa-exclamation-triangle'
      })
    } finally {
      this.isOpeningArticleModal = false
    }
  },
  createSurvey () {
    if (!this.hasSurveyEditionRights) {
      this.showPermissionDenied('Vous n\'avez pas les droits pour créer un sondage.')
      return
    }
    if (this.isOpeningSurveyModal) {
      return
    }
    this.isOpeningSurveyModal = true
    try {
      const modal = this.$buefy.modal.open({
        parent: this,
        component: OneGroup,
        hasModalCard: true,
        width: 960,
        canCancel: ['escape', 'outside'],
        destroyOnHide: false,
        ariaRole: 'dialog',
        ariaModal: true,
        customClass: 'dashboard-modal content-editor-modal-host',
        props: {
          editGroup: null
        },
        events: {
          created: (createdGroupData) => {
            modal.close()
            this.handleSurveyCreated(createdGroupData, { promptConfiguration: false })
          }
        }
      })
    } catch (error) {
      this.$buefy.notification.open({
        duration: 4000,
        message: 'Impossible d\'ouvrir la création de sondage pour le moment.',
        position: 'is-top-right',
        type: 'is-danger',
        hasIcon: true,
        iconPack: 'fas',
        icon: 'fas fa-exclamation-triangle'
      })
    } finally {
      this.isOpeningSurveyModal = false
    }
  },
  async openArticle (article) {
    if (!article || !article.uuid) {
      return
    }
    if (!this.hasNewsEditionRights) {
      this.showPermissionDenied('Vous n\'avez pas les droits pour modifier une actualité.')
      return
    }
    if (this.isOpeningArticleModal) {
      return
    }
    this.isOpeningArticleModal = true
    try {
      await this.openArticleModalOnDashboard({
        article,
        articleUuid: article.uuid
      })
    } catch (error) {
      this.$buefy.notification.open({
        duration: 4000,
        message: 'Impossible d\'ouvrir cette actualité pour le moment.',
        position: 'is-top-right',
        type: 'is-danger',
        hasIcon: true,
        iconPack: 'fas',
        icon: 'fas fa-exclamation-triangle'
      })
    } finally {
      this.isOpeningArticleModal = false
    }
  },
  ensureSurveyGroupCache () {
    if (!this._surveyGroupDetailsCache) {
      this._surveyGroupDetailsCache = new Map()
    }
    return this._surveyGroupDetailsCache
  },
  cloneSurveyPayload (survey) {
    if (!survey || typeof survey !== 'object') {
      return null
    }
    try {
      const cloned = JSON.parse(JSON.stringify(survey))
      const optionsArray = Array.isArray(cloned.options) ? cloned.options : []
      cloned.options = optionsArray.map(option => {
        if (option && typeof option === 'object') {
          return {
            uuid: option.uuid || null,
            option: option.option !== undefined ? option.option : option.value || ''
          }
        }
        return {
          uuid: null,
          option: typeof option === 'string' ? option : ''
        }
      })
      return cloned
    } catch (error) {
      const fallback = { ...survey }
      fallback.options = Array.isArray(fallback.options)
        ? fallback.options.map(option => {
          if (option && typeof option === 'object') {
            return {
              uuid: option.uuid || null,
              option: option.option !== undefined ? option.option : option.value || ''
            }
          }
          return {
            uuid: null,
            option: typeof option === 'string' ? option : ''
          }
        })
        : []
      return fallback
    }
  },
  cloneSurveyGroupPayload (group) {
    if (!group || typeof group !== 'object') {
      return null
    }
    let normalized
    try {
      normalized = JSON.parse(JSON.stringify(group))
    } catch (error) {
      normalized = { ...group }
    }

    const surveysArray = Array.isArray(normalized?.surveys) ? normalized.surveys : []
    normalized.surveys = surveysArray
      .map(survey => this.cloneSurveyPayload(survey))
      .filter(Boolean)

    normalized.groups = Array.isArray(normalized.groups) ? [...normalized.groups] : []

    return normalized
  },
  storeSurveyGroupInCache (group) {
    if (!group || typeof group !== 'object') {
      return
    }
    const cache = this.ensureSurveyGroupCache()
    const normalized = this.cloneSurveyGroupPayload(group)
    if (!normalized) {
      return null
    }
    if (normalized.uuid) {
      cache.set(normalized.uuid, normalized)
    }
    const surveys = Array.isArray(normalized.surveys) ? normalized.surveys : []
    surveys.forEach(survey => {
      if (survey && survey.uuid) {
        cache.set(survey.uuid, normalized)
      }
    })
    return this.cloneSurveyGroupPayload(normalized)
  },
  async resolveSurveyGroupByUuid (uuid) {
    if (!uuid) {
      return null
    }
    const cache = this.ensureSurveyGroupCache()
    if (cache.has(uuid)) {
      return this.cloneSurveyGroupPayload(cache.get(uuid))
    }
    try {
      const { data } = await http.get('/domain/surveys', {
        params: {
          multiple: true,
          past: true
        }
      })
      if (Array.isArray(data)) {
        data.forEach(entry => {
          this.storeSurveyGroupInCache(entry)
        })
        if (cache.has(uuid)) {
          return this.cloneSurveyGroupPayload(cache.get(uuid))
        }
        return null
      }
    } catch (error) {
      throw error
    }
    return null
  },
  openSurveyGroupModal (group, { origin = 'pending' } = {}) {
    if (!group) {
      return null
    }
    const payload = this.storeSurveyGroupInCache(group) || this.cloneSurveyGroupPayload(group)
    const modalPayload = this.cloneSurveyGroupPayload(payload)
    const modal = this.$buefy.modal.open({
      parent: this,
      component: OneGroup,
      hasModalCard: true,
      customClass: 'content-editor-modal-host',
      width: 960,
      canCancel: ['escape', 'outside'],
      destroyOnHide: false,
      ariaRole: 'dialog',
      ariaModal: true,
      props: {
        editGroup: modalPayload
      },
      events: {
        updated: (updatedGroupData) => {
          modal.close()
          this.handleSurveyGroupUpdated(updatedGroupData, { origin })
        },
        deleted: () => {
          modal.close()
          this.handleSurveyGroupDeleted({ origin })
        }
      }
    })
    return modal
  },
  extractSurveyGroupEnabledFlag (groupData) {
    if (!groupData) {
      return false
    }
    if (typeof groupData?.group?.is_enabled === 'boolean') {
      return groupData.group.is_enabled
    }
    if (typeof groupData?.is_enabled === 'boolean') {
      return groupData.is_enabled
    }
    return false
  },
  handleSurveyGroupUpdated (updatedGroupData, { origin = 'pending' } = {}) {
    const payload = updatedGroupData?.group || updatedGroupData || null
    if (payload) {
      this.storeSurveyGroupInCache(payload)
    }
    this.refreshSurveysSnapshot({ includePast: true })
    this.fetchMavieproSurveys().catch(() => {})

    const isEnabled = this.extractSurveyGroupEnabledFlag(updatedGroupData)
    let message = 'Sondage mis à jour avec succès.'
    if (isEnabled) {
      message = 'Sondage activé avec succès.'
    }

    this.$buefy.notification.open({
      duration: 4000,
      message,
      position: 'is-bottom-right',
      type: 'is-success',
      hasIcon: true,
      icon: 'check'
    })
  },
  handleSurveyGroupDeleted ({ origin = 'pending' } = {}) {
    this.refreshSurveysSnapshot({ includePast: true })
    this.fetchMavieproSurveys().catch(() => {})
    this.$buefy.notification.open({
      duration: 4000,
      message: 'Sondage supprimé avec succès.',
      position: 'is-bottom-right',
      type: 'is-success',
      hasIcon: true,
      icon: 'check'
    })
  },
  duplicateMavieproArticle (article) {
    if (!article || !article.uuid || this.duplicatingMavieproArticle) {
      return
    }
    if (!this.hasNewsEditionRights) {
      this.showPermissionDenied('Vous n\'avez pas les droits pour dupliquer une actualité.')
      return
    }

    const title = article.title || 'Cette actualité'

    this.$buefy.dialog.confirm({
      title: 'Duplication de l\'actualité',
      message: `Voulez-vous vraiment dupliquer l'actualité <strong>${title}</strong> ?<br><br>En confirmant, elle sera automatiquement dupliquée sur votre domaine et son édition s'ouvrira dans une fenêtre dédiée.`,
      confirmText: 'Dupliquer',
      cancelText: 'Annuler',
      type: 'is-danger',
      onConfirm: () => {
        this.duplicatingMavieproArticle = true
        http.post(`/article/${article.uuid}/duplicate`).then(({ data }) => {
          this.duplicatingMavieproArticle = false
          const newArticleUuid = data?.uuid
          if (newArticleUuid) {
            this.$buefy.notification.open({
              duration: 4000,
              message: 'Actualité dupliquée avec succès !',
              position: 'is-bottom-right',
              type: 'is-success',
              hasIcon: true,
              icon: 'check'
            })
            this.fetchPendingNews()
            this.fetchMavieproNews().catch(() => {})
            this.openArticleModalOnDashboard({
              article: data,
              articleUuid: newArticleUuid
            }).catch(() => {
              this.$buefy.notification.open({
                duration: 4000,
                message: 'La duplication est réussie mais l\'édition n\'a pas pu être ouverte automatiquement.',
                position: 'is-top-right',
                type: 'is-warning',
                hasIcon: true,
                iconPack: 'fas',
                icon: 'fas fa-exclamation-triangle'
              })
            })
          } else {
            this.fetchPendingNews()
            this.fetchMavieproNews().catch(() => {})
          }
        }).catch(() => {
          this.duplicatingMavieproArticle = false
          this.$buefy.notification.open({
            duration: 4000,
            message: 'La duplication de l\'actualité a échoué.',
            position: 'is-top-right',
            type: 'is-danger',
            hasIcon: true,
            iconPack: 'fas',
            icon: 'fas fa-exclamation-triangle'
          })
        })
      }
    })
  },
  async activatePendingSurvey (survey) {
    const targetUuid = survey?.groupUuid || survey?.uuid
    if (!targetUuid) {
      return
    }
    if (!this.hasSurveyEditionRights) {
      this.showPermissionDenied('Vous n\'avez pas les droits pour modifier un sondage.')
      return
    }

    try {
      const group = await this.resolveSurveyGroupByUuid(targetUuid)
      if (!group) {
        this.$buefy.notification.open({
          duration: 4000,
          message: 'Impossible de récupérer les détails de ce sondage.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-exclamation-triangle'
        })
        return
      }

      try {
        this.openSurveyGroupModal(group, { origin: 'pending' })
      } catch (modalError) {
        this.$buefy.notification.open({
          duration: 4000,
          message: 'Le sondage a été récupéré mais l\'édition n\'a pas pu s\'ouvrir.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-exclamation-triangle'
        })
      }
    } catch (error) {
      this.$buefy.notification.open({
        duration: 4000,
        message: 'Impossible d\'ouvrir ce sondage pour le moment.',
        position: 'is-top-right',
        type: 'is-danger',
        hasIcon: true,
        iconPack: 'fas',
        icon: 'fas fa-exclamation-triangle'
      })
    }
  },
  duplicateMavieproSurvey (survey) {
    if (!survey || !survey.uuid || this.duplicatingMavieproSurvey) {
      return
    }
    if (!this.hasSurveyEditionRights) {
      this.showPermissionDenied('Vous n\'avez pas les droits pour dupliquer un sondage.')
      return
    }

    const title = survey.title || 'Ce sondage'

    this.$buefy.dialog.confirm({
      title: 'Duplication du sondage',
      message: `Voulez-vous vraiment dupliquer le sondage <strong>${title}</strong> ?<br><br>En confirmant, il sera automatiquement dupliqué sur votre domaine et son édition s'ouvrira dans une fenêtre dédiée.`,
      confirmText: 'Dupliquer',
      cancelText: 'Annuler',
      type: 'is-info',
      onConfirm: () => {
        this.duplicatingMavieproSurvey = true

        const loadingNotification = this.$buefy.notification.open({
          duration: 0,
          message: 'Duplication en cours...',
          position: 'is-bottom-right',
          type: 'is-info',
          hasIcon: true,
          icon: 'loading',
          indefinite: true
        })

        http.post(`/surveys/${survey.uuid}/duplicate`).then(({ data }) => {
          loadingNotification.close()
          this.duplicatingMavieproSurvey = false

          const newGroup = data && data.group ? data.group : null

          this.$buefy.notification.open({
            duration: 4000,
            message: 'Sondage dupliqué avec succès !',
            position: 'is-bottom-right',
            type: 'is-success',
            hasIcon: true,
            icon: 'check'
          })

          this.refreshSurveysSnapshot({ includePast: true })
          this.fetchMavieproSurveys().catch(() => {})

          if (newGroup) {
            try {
              this.openSurveyGroupModal(newGroup, { origin: 'maviepro' })
            } catch (modalError) {
              this.$buefy.notification.open({
                duration: 4000,
                message: 'Le sondage est dupliqué mais l\'édition n\'a pas pu s\'ouvrir automatiquement.',
                position: 'is-top-right',
                type: 'is-warning',
                hasIcon: true,
                iconPack: 'fas',
                icon: 'fas fa-exclamation-triangle'
              })
            }
          }
        }).catch(() => {
          loadingNotification.close()
          this.duplicatingMavieproSurvey = false
          this.$buefy.notification.open({
            duration: 4000,
            message: 'Erreur lors de la duplication du sondage.',
            position: 'is-bottom-right',
            type: 'is-danger',
            hasIcon: true,
            icon: 'alert'
          })
        })
      }
    })
  },
  promptSurveyConfiguration (newGroup) {
    if (!newGroup) {
      return
    }

    setTimeout(() => {
      const displayTitle = (newGroup.group_title && newGroup.group_title.trim())
        ? newGroup.group_title
        : (newGroup.surveys && newGroup.surveys[0] && newGroup.surveys[0].question ? newGroup.surveys[0].question : '(Sans titre)')

      this.$buefy.dialog.confirm({
        title: 'Configuration du sondage dupliqué',
        message: `Voulez-vous configurer les paramètres du sondage "${displayTitle}" ?`,
        confirmText: 'Configurer',
        cancelText: 'Plus tard',
        onConfirm: () => {
          this.$buefy.modal.open({
            parent: this,
            component: OneGroup,
            hasModalCard: true,
            customClass: 'content-editor-modal-host',
            width: 960,
            canCancel: ['escape', 'outside'],
            destroyOnHide: false,
            ariaRole: 'dialog',
            ariaModal: true,
            props: {
              editGroup: newGroup
            },
            events: {
              updated: (updatedGroupData) => {
                let isEnabled = false
                if (updatedGroupData?.group?.is_enabled !== undefined) {
                  isEnabled = updatedGroupData.group.is_enabled
                } else if (updatedGroupData?.is_enabled !== undefined) {
                  isEnabled = updatedGroupData.is_enabled
                }

                const targetStatus = isEnabled ? 'active' : 'inactive'
                const currentStatus = this.$route.query.status || 'active'

                if (targetStatus !== currentStatus) {
                  this.$router.push({
                    path: '/app/multisurveys',
                    query: { status: targetStatus }
                  }).catch(() => {})
                }

                this.$buefy.notification.open({
                  duration: 4000,
                  message: 'Sondage mis à jour avec succès.',
                  position: 'is-bottom-right',
                  type: 'is-success',
                  hasIcon: true,
                  icon: 'check'
                })
              }
            }
          })
        }
      })
    }, 300)
  },
  handleSurveyCreated (createdGroupData, { promptConfiguration = false } = {}) {
    if (!createdGroupData) {
      return
    }

    const newGroup = createdGroupData?.group || createdGroupData || null

    if (promptConfiguration && newGroup) {
      this.promptSurveyConfiguration(newGroup)
    }

    const surveysArray = Array.isArray(newGroup?.surveys) ? newGroup.surveys : []
    const firstSurvey = surveysArray.length ? surveysArray[0] : null

    const resolveBoolean = (value) => typeof value === 'boolean' ? value : undefined
    const resolvedEnabled = resolveBoolean(newGroup?.is_enabled) ?? resolveBoolean(firstSurvey?.is_enabled) ?? false

    const rawStart = newGroup?.start_at || newGroup?.startAt || firstSurvey?.start_at || firstSurvey?.startAt || null
    const startDate = rawStart ? new Date(rawStart) : null
    const startIsValid = startDate instanceof Date && !Number.isNaN(startDate.getTime())
    const now = new Date()
    const startsNowOrPast = startIsValid ? startDate.getTime() <= now.getTime() : true

    const shouldRefreshActive = resolvedEnabled && startsNowOrPast

    this.refreshSurveysSnapshot({ includePast: true })

    let message = 'Sondage créé avec succès.'
    if (shouldRefreshActive) {
      message = 'Sondage créé avec succès. Il est disponible dans « Sondages en cours ».'
    } else if (resolvedEnabled) {
      message = 'Sondage créé avec succès. Son activation suivra la date programmée.'
    } else {
      message = 'Sondage créé avec succès. Vous pouvez finaliser sa configuration dans « Sondages à activer ».'
    }

    this.$buefy.notification.open({
      duration: 4000,
      message,
      position: 'is-bottom-right',
      type: 'is-success',
      hasIcon: true,
      icon: 'check'
    })
  },
  showPermissionDenied (message) {
    this.$buefy.notification.open({
      duration: 4000,
      message,
      position: 'is-bottom-right',
      type: 'is-danger',
      hasIcon: true,
      iconPack: 'fas',
      icon: 'fas fa-lock'
    })
  },
  normalizeSurveyGroup (group) {
    if (!group) {
      return null
    }

    const surveysArray = Array.isArray(group.surveys) ? group.surveys : []
    const firstSurvey = surveysArray.length ? surveysArray[0] : null

    const parseDateSafe = (value) => {
      if (!value) {
        return null
      }
      const date = value instanceof Date ? value : new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    }

    const coerceBoolean = (value) => {
      if (typeof value === 'boolean') {
        return value
      }
      if (typeof value === 'number') {
        return value === 1
      }
      if (typeof value === 'string') {
        return value === '1' || value.toLowerCase() === 'true'
      }
      return false
    }

    const startAt = parseDateSafe(group.start_at || firstSurvey?.start_at)
    const endAt = parseDateSafe(group.end_at || firstSurvey?.end_at)
    const updatedAt = parseDateSafe(firstSurvey?.updated_at || group.updated_at)
    const isEnabled = coerceBoolean(group.is_enabled) || coerceBoolean(firstSurvey?.is_enabled)
    const isNotified = coerceBoolean(group.is_notified) || coerceBoolean(firstSurvey?.is_notified)
    const isSingle = Boolean(group.is_single)

    const title = (group.group_title && group.group_title.trim())
      ? group.group_title
      : (firstSurvey?.question || 'Sondage')

    const surveysCount = surveysArray.length
    const exportId = group.uuid ?? firstSurvey?.uuid ?? group.id ?? firstSurvey?.id ?? null
    const uuid = group.uuid ?? firstSurvey?.uuid ?? null

    const periodLabel = this.formatSurveyPeriod(startAt, endAt)

    const coerceCount = (value) => {
      const parsed = Number(value)
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
    }

    const statsSource = group.stats || {}
    const stats = {
      fullAnswers: coerceCount(statsSource.fullAnswers),
      partialAnswers: coerceCount(statsSource.partialAnswers)
    }

    return {
      uuid,
      exportId,
      title,
      surveysCount,
      startAt,
      endAt,
      startLabel: this.formatDateTime(startAt),
      endLabel: this.formatDateTime(endAt),
      periodLabel,
      updatedAt,
      isEnabled,
      isNotified,
      isSingle,
      stats
    }
  },
  isSurveyActive (survey, referenceDate = new Date()) {
    if (!survey || !survey.isEnabled) {
      return false
    }
    const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate)
    if (survey.startAt && survey.startAt.getTime() > ref.getTime()) {
      return false
    }
    if (survey.endAt && survey.endAt.getTime() < ref.getTime()) {
      return false
    }
    return true
  },
  formatDateTime (value) {
    if (!value) {
      return null
    }
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) {
      return null
    }
    return format(date, 'dd/MM/yyyy à HH:mm', { locale: fr })
  },
  formatSurveyPeriod (startAt, endAt) {
    const startLabel = this.formatDateTime(startAt)
    const endLabel = this.formatDateTime(endAt)
    if (startLabel && endLabel) {
      return `Du ${startLabel} au ${endLabel}`
    }
    if (startLabel) {
      return `Débute le ${startLabel}`
    }
    if (endLabel) {
      return `Se termine le ${endLabel}`
    }
    return null
  },
  formatPublicationDate (value) {
    if (!value) {
      return 'Date à confirmer'
    }
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return value
    }
    return format(date, 'dd/MM/yyyy à HH:mm', { locale: fr })
  },
  toggleLine (key) {
    if (this.isLayoutEditMode) {
      return
    }
    if (!this.collapsedLines || !Object.prototype.hasOwnProperty.call(this.collapsedLines, key)) {
      return
    }
    const nextValue = !this.collapsedLines[key]
    this.$set(this.collapsedLines, key, nextValue)
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(`dash_collapsed_line_${key}`, JSON.stringify(nextValue))
      } catch (error) {
        // Silently ignore storage errors (private mode, quota, ...)
      }
    }
  },
  initializeCardOrders () {
    const defaults = this.defaultRightCardOrder
    if (typeof window === 'undefined') {
      this.rightCardOrder = defaults.slice()
      return
    }
    try {
      const stored = JSON.parse(window.localStorage.getItem('dash_right_card_order') || '[]')
      this.rightCardOrder = this.normalizeOrder(Array.isArray(stored) ? stored : [], defaults)
    } catch (error) {
      this.rightCardOrder = defaults.slice()
    }
  },
  normalizeOrder (order, defaults) {
    const filtered = []
    order.forEach(id => {
      if (defaults.includes(id) && !filtered.includes(id)) {
        filtered.push(id)
      }
    })
    defaults.forEach(id => {
      if (!filtered.includes(id)) {
        filtered.push(id)
      }
    })
    return filtered
  },
  saveCardOrder (column) {
    if (typeof window === 'undefined') {
      return
    }
    if (column === 'right') {
      window.localStorage.setItem('dash_right_card_order', JSON.stringify(this.rightCardOrder))
    }
  },
  toggleLayoutMode () {
    this.isLayoutEditMode = !this.isLayoutEditMode
  },
  onDragStart (column, id, event) {
    if (!this.isLayoutEditMode) {
      return
    }
    if (event && event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', id)
    }
    this.draggingColumn = column
    this.draggingId = id
  },
  onDragEnter (column, targetId) {
    if (!this.isLayoutEditMode || this.draggingColumn !== column || !this.draggingId) {
      return
    }
    if (column === 'right') {
      const order = this.rightCardOrder.slice()
      const fromIndex = order.indexOf(this.draggingId)
      if (fromIndex === -1) {
        return
      }
      if (!targetId) {
        order.splice(fromIndex, 1)
        order.push(this.draggingId)
        this.rightCardOrder = order
        return
      }
      const toIndex = order.indexOf(targetId)
      if (toIndex === -1 || fromIndex === toIndex) {
        return
      }
      order.splice(fromIndex, 1)
      const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
      order.splice(adjustedIndex, 0, this.draggingId)
      this.rightCardOrder = order
    }
  },
  onDrop (column) {
    if (!this.isLayoutEditMode || this.draggingColumn !== column) {
      this.draggingColumn = null
      this.draggingId = null
      return
    }
    if (column === 'right') {
      this.saveCardOrder('right')
    }
    this.draggingColumn = null
    this.draggingId = null
  },
  setDomainStartDate (setStart) {
    if (!this.domain || !this.domain.uuid) {
      return Promise.resolve(false)
    }

    const domainUuid = this.domain.uuid
    const fallbackDomain = this.domain

    return http.get(`/domain/${domainUuid}`)
      .then(({ data }) => data || fallbackDomain)
      .catch(() => this.domain?.uuid === domainUuid ? this.domain : fallbackDomain)
      .then((domain) => {
        if (this.domain?.uuid !== domainUuid) {
          return false
        }

        this.domainStartDate = getDomainPeriodStartDate(domain)
        if (setStart) {
          this.updateToday()
          this.dateStart = this.domainStartDate > this.todayFormatted
            ? this.todayFormatted
            : this.domainStartDate
          this.dateEnd = this.todayFormatted
          this.dateRange = [this.dateStart, this.dateEnd]
        }
        this.syncQuickRange()

        return true
      })
  },
  getStats () {
    this.loaded = false
    this.loadingGraphs = true
    this.loadingPopularities = true
    const employeeActivityPromise = this.fetchEmployeeActivityGeneralSnapshot() || Promise.resolve()
    const topNewsPromise = this.fetchTopNewsSnapshot() || Promise.resolve()
    const popularityPromise = this.fetchPopularity() || Promise.resolve()
    const readTrackingPromise = this.fetchReadTrackingSnapshot() || Promise.resolve()

    Promise.all([employeeActivityPromise, topNewsPromise, popularityPromise, readTrackingPromise])
      .finally(() => {
        this.loadingGraphs = false
        this.loaded = true
      })
  },
  resetEmployeeActivitySnapshot () {
    this.employeeActivityTotals = createEmptyEmployeeActivityTotals()
    this.employeeActivityDailySeries = []
    this.chartUsersData = null
    this.chartLoginsData = null
    this.loginsSeries = []

    if (this.counters && this.counters.total && this.counters.period) {
      this.counters.total.users = 0
      this.counters.period.users = 0
      this.counters.period.loginsUniq = 0
    }
  },
  fetchEmployeeActivityGeneralSnapshot () {
    const params = this.buildDateRangeParams({
      trackingYear: Number(String(this.dateEnd || '').slice(0, 4)) || new Date().getFullYear()
    })

    if (this.domain && this.domain.uuid) {
      params.domains = [this.domain.uuid]
    }

    this.resetEmployeeActivitySnapshot()

    return http.get('/dashboard/employee-activity-general', { params })
      .then(({ data }) => {
        const totals = normalizeEmployeeActivityTotals(data?.totals)
        const dailySeries = normalizeEmployeeActivityDailySeries(data?.dailySeries)

        this.employeeActivityTotals = totals
        this.employeeActivityDailySeries = dailySeries
        this.loginsSeries = dailySeries.map(point => ({
          date: point.date,
          unique: point.activeUsers,
          total: point.activeLogins
        }))

        this.counters.total.users = totals.downloadsCumulative
        this.counters.period.users = totals.downloadsPeriod
        this.counters.period.loginsUniq = totals.activeUsers

        if (!Number(this.mockEmployeeTargets.manualOverride) && totals.workforce > 0) {
          this.mockEmployeeTargets.totalEmployees = totals.workforce
        }
      })
      .catch(() => {
        this.resetEmployeeActivitySnapshot()
        this.$buefy.notification.open({
          duration: 5000,
          message: 'Impossible de récupérer les statistiques générales pour le moment.',
          position: 'is-top-right',
          type: 'is-warning',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-server'
        })
      })
  },
  openMonthlyTracking () {
    this.isMonthlyTrackingOpen = true
    this.monthlyTrackingSeries = []
    this.loadMonthlyTracking()
  },
  closeMonthlyTracking () {
    this.isMonthlyTrackingOpen = false
  },
  loadMonthlyTracking () {
    this.isMonthlyTrackingLoading = true

    const params = this.buildDateRangeParams({
      trackingYear: this.selectedTrackingYear,
      monthlyOnly: 1
    })

    if (this.domain && this.domain.uuid) {
      params.domains = [this.domain.uuid]
    }

    return Promise.all([
      http.get('/dashboard/employee-activity-general', { params }),
      http.get('/dashboard/read-tracking', { params }),
      http.get('/dashboard/elected-published-contents', {
        params: {
          ...params,
          type: 'article'
        }
      })
    ])
      .then(([employeeResponse, readsResponse, publishedResponse]) => {
        const employeeSeries = Array.isArray(employeeResponse?.data?.monthlySeries)
          ? employeeResponse.data.monthlySeries
          : []
        const readsSeries = Array.isArray(readsResponse?.data?.monthlySeries)
          ? readsResponse.data.monthlySeries
          : []
        const publishedSeries = Array.isArray(publishedResponse?.data?.monthlySeries)
          ? publishedResponse.data.monthlySeries
          : []

        this.monthlyTrackingSeries = Array.from({ length: 12 }, (_, index) => {
          const month = index + 1
          const employee = employeeSeries.find(row => Number(row?.month) === month) || {}
          const reads = readsSeries.find(row => Number(row?.month) === month) || {}
          const published = publishedSeries.find(row => Number(row?.month) === month) || {}

          return {
            ...employee,
            month,
            allReads: Number(reads.allReads || 0),
            articleReads: Number(reads.articleReads || 0),
            faqReads: Number(reads.faqReads || 0),
            publishedNews: Number(published.allContents || 0)
          }
        })
      })
      .catch(() => {
        this.monthlyTrackingSeries = []
      })
      .finally(() => {
        this.isMonthlyTrackingLoading = false
      })
  },
  fetchReadTrackingSnapshot () {
    const params = this.buildDateRangeParams()
    if (this.domain && this.domain.uuid) {
      params.domains = [this.domain.uuid]
    }

    this.loadingReadTracking = true
    this.readTrackingDailySeries = []
    this.readTrackingTotals = null

    return http.get('/dashboard/read-tracking', { params })
      .then(({ data }) => {
        this.readTrackingDailySeries = Array.isArray(data?.dailySeries) ? data.dailySeries : []
        const totals = data?.totals || {}
        const articleReads = Number(totals.articleReads)
        const faqReads = Number(totals.faqReads)
        const totalReads = Number(totals.totalReads)

        this.readTrackingTotals = {
          articleReads: Number.isFinite(articleReads) && articleReads >= 0 ? articleReads : 0,
          faqReads: Number.isFinite(faqReads) && faqReads >= 0 ? faqReads : 0,
          totalReads: Number.isFinite(totalReads) && totalReads >= 0
            ? totalReads
            : ((Number.isFinite(articleReads) ? articleReads : 0) + (Number.isFinite(faqReads) ? faqReads : 0))
        }
      })
      .catch(() => {
        this.readTrackingDailySeries = []
        this.readTrackingTotals = null
      })
      .finally(() => {
        this.loadingReadTracking = false
      })
  },
  fetchPopularity () {
    const params = this.buildDateRangeParams()
    return http.get('/dashboard/popularity', { params })
      .then(({ data }) => {
        this.popularity = {
          faqs: Array.isArray(data?.faqs) ? data.faqs : [],
          searches: Array.isArray(data?.searches) ? data.searches : [],
          categories: Array.isArray(data?.categories) ? data.categories : [],
          totals: data?.totals || {}
        }
        const periodFaqViews = Number(data?.totals?.faqViews?.period)
        this.counters.period = {
          ...this.counters.period,
          faqViews: Number.isFinite(periodFaqViews) && periodFaqViews >= 0 ? periodFaqViews : 0
        }
      })
      .catch(() => {
        this.popularity = { faqs: [], searches: [], categories: [], totals: {} }
        this.counters.period = {
          ...this.counters.period,
          faqViews: 0
        }
      })
      .finally(() => {
        this.loadingPopularities = false
      })
  },
  downloadLastSurveyStats () {
    if (!this.lastEndedSurvey || !this.lastEndedSurvey.uuid || this.isDownloadingLastSurveyStats) {
      return
    }

    const exportUuid = this.lastEndedSurvey.exportId || this.lastEndedSurvey.uuid
    if (!exportUuid) {
      return
    }

    const endpoint = this.lastEndedSurvey.isSingle
      ? `/surveys/${exportUuid}/stats`
      : `/surveys/${exportUuid}/groupstats`

    this.isDownloadingLastSurveyStats = true
    http.get(endpoint, { responseType: 'blob' })
      .then(({ data }) => {
        const blob = data instanceof Blob ? data : new Blob([data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `stats_${exportUuid}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => window.URL.revokeObjectURL(url), 100)
      })
      .catch(() => {
        this.$buefy.notification.open({
          duration: 6000,
          message: 'Le téléchargement des statistiques du dernier sondage a échoué. Merci de réessayer ultérieurement.',
          position: 'is-top-right',
          type: 'is-danger',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-exclamation-triangle'
        })
      })
      .finally(() => {
        this.isDownloadingLastSurveyStats = false
      })
  },
  downloadSearchExport () {
    if (this.isLoadingExport) {
      return
    }
    this.isLoadingExport = true
    http.get('/domain/export/search', { responseType: 'blob' })
      .then(({ data }) => {
        const blob = data instanceof Blob ? data : new Blob([data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        const domainLabel = (this.domain && this.domain.label) ? this.domain.label : 'recherches'
        link.href = url
        link.setAttribute('download', `${domainLabel}-recherches.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => window.URL.revokeObjectURL(url), 100)
      })
      .catch(() => {
        this.$buefy.notification.open({
          duration: 4000,
          message: 'L\'export des recherches a échoué. Merci de réessayer ultérieurement.',
          position: 'is-top-right',
          type: 'is-danger',
          hasIcon: true,
          iconPack: 'fas',
          icon: 'fas fa-exclamation-triangle'
        })
      })
      .finally(() => {
        this.isLoadingExport = false
      })
  },
  buildDateRangeParams (initial = {}) {
    const params = { ...initial }
    if (this.dateStart) {
      params.dateStart = this.dateStart
    }
    if (this.dateEnd) {
      params.dateEnd = this.dateEnd
    }
    return params
  },
  isFullRangeSelected () {
    if (!this.domainStartDate || !this.todayFormatted || !this.dateStart || !this.dateEnd) {
      return false
    }
    return this.dateStart <= this.domainStartDate && this.dateEnd >= this.todayFormatted
  },
  handleDateBoundaryChange (storageKey, value) {
    if (!value) {
      return
    }
    window.localStorage.setItem(storageKey, value)
    this.syncQuickRange()
    this.loadingGraphs = true
    this.topNewsItems = []
    this.loadingTopNews = true
    this.loginsSeries = []
    this.loadingPopularities = true
    this.debounceUpdateRequest()
  },
  updateRequest () {
    this.getStats()
  },
  applyQuickRange (range) {
    if (!range) {
      return
    }
    this.updateToday()
    const endDate = this.today
    const startDate = this.calculateRangeStart(endDate, range)
    const start = format(startDate, 'yyyy-MM-dd')
    const end = format(endDate, 'yyyy-MM-dd')
    this.activeQuickRangeId = range.id
    this.dateRange = [start, end]
  },
  showFull () {
    if (!this.domainStartDate) {
      return
    }
    this.updateToday()
    const end = format(this.today, 'yyyy-MM-dd')
    const start = this.domainStartDate > end ? end : this.domainStartDate
    this.activeQuickRangeId = 'full'
    this.dateRange = [start, end]
  },
  calculateRangeStart (endDate, range) {
    switch (range.type) {
      case 'day':
        return subDays(endDate, range.amount)
      case 'week':
        return subWeeks(endDate, range.amount)
      case 'month':
        return subMonths(endDate, range.amount)
      default:
        return subWeeks(endDate, 1)
    }
  },
  syncQuickRange () {
    const todayString = format(this.today, 'yyyy-MM-dd')
    const fullRangeStart = this.domainStartDate > todayString ? todayString : this.domainStartDate
    if (fullRangeStart && this.dateStart === fullRangeStart && this.dateEnd === todayString) {
      this.activeQuickRangeId = 'full'
      return
    }
    if (this.dateEnd !== todayString) {
      this.activeQuickRangeId = null
      return
    }
    const endDate = parseISO(this.dateEnd)
    for (const range of this.quickRanges) {
      const expectedStart = format(this.calculateRangeStart(endDate, range), 'yyyy-MM-dd')
      if (expectedStart === this.dateStart) {
        this.activeQuickRangeId = range.id
        return
      }
    }
    this.activeQuickRangeId = null
  },
  updateToday () {
    this.today = new Date()
    this.todayFormatted = format(this.today, 'yyyy-MM-dd')
  },
  toggleAlertKeyword (alert) {
    if (this.selectedAlertKeyword && this.selectedAlertKeyword.word === alert.word) {
      this.selectedAlertKeyword = null
      return
    }
    this.selectedAlertKeyword = alert
  },
  normalizeKeyword (value) {
    if (!value) {
      return ''
    }
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s-]/g, '')
      .toLowerCase()
  },
  formatNumber (value) {
    if (value === null || value === undefined) {
      return '0'
    }
    return this.numberFormatter.format(value)
  },
  formatPercent (value) {
    return `${new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Number(value || 0))} %`
  },
  formatMonthlyValue (value, format) {
    if (format === 'percent') {
      return this.formatPercent(value)
    }

    return this.formatNumber(value)
  },
  isFutureTrackingMonth (monthNumber) {
    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth() + 1
    return this.selectedTrackingYear > currentYear ||
      (this.selectedTrackingYear === currentYear && Number(monthNumber) > currentMonth)
  }
}
